/**
 * ColumnPicker — drag-to-reorder + eye visibility toggle (canonical table chrome).
 * ColumnsControl — the "Columns" button that opens it: the item list's toolbar
 * control, also drawn above the Report Designer's rows.
 */

import { useRef, useState } from 'react';
import { Columns3, Eye, EyeOff, GripVertical } from 'lucide-react';

export interface ColumnPickerColumn {
  key: string;
  label: string;
}

export interface ColumnPickerProps {
  columns: ColumnPickerColumn[];
  visibleColumns: string[];
  onToggleColumn: (key: string) => void;
  onReorderColumns: (newOrder: string[]) => void;
  onClose: () => void;
}

export function ColumnPicker({
  columns,
  visibleColumns,
  onToggleColumn,
  onReorderColumns,
  onClose,
}: ColumnPickerProps) {
  const dragSrcIdx = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const hiddenCols = columns.filter((c) => !visibleColumns.includes(c.key));
  const visibleDefs = visibleColumns
    .map((key) => columns.find((c) => c.key === key))
    .filter(Boolean) as ColumnPickerColumn[];

  const handleDragStart = (idx: number) => {
    dragSrcIdx.current = idx;
  };
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };
  const handleDrop = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragSrcIdx.current === null || dragSrcIdx.current === idx) return;
    const next = [...visibleColumns];
    const [moved] = next.splice(dragSrcIdx.current, 1);
    next.splice(idx, 0, moved);
    onReorderColumns(next);
    dragSrcIdx.current = null;
    setDragOverIdx(null);
  };
  const handleDragEnd = () => {
    dragSrcIdx.current = null;
    setDragOverIdx(null);
  };

  return (
    <>
      <div className="fixed inset-0 z-10" onClick={onClose} />
      <div className="absolute right-0 top-full mt-1 w-60 bg-[var(--bg-raised)] shadow-lg shadow-gray-200/50 border border-[var(--rule)]/80 z-20 overflow-hidden">
        {visibleDefs.length > 0 && (
          <div className="py-1">
            <div className="px-3 py-1.5 text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-widest">
              Visible — drag to reorder
            </div>
            {visibleDefs.map((col, idx) => (
              <div
                key={col.key}
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDrop={(e) => handleDrop(e, idx)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-2 px-2 py-1.5 cursor-grab transition-colors ${
                  dragOverIdx === idx ? 'bg-[var(--slate-dim)] border-t-2 border-[var(--slate)]' : 'hover:bg-[var(--surface)]'
                }`}
              >
                <GripVertical className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                <span className="flex-1 text-sm text-[var(--text-pri)]">{col.label}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleColumn(col.key);
                  }}
                  className="p-0.5 rounded hover:bg-[var(--hover-tint)] text-[var(--text-ter)] transition-colors"
                  title="Hide column"
                >
                  <Eye className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {hiddenCols.length > 0 && (
          <div className={`py-1 ${visibleDefs.length > 0 ? 'border-t border-[var(--rule)]' : ''}`}>
            <div className="px-3 py-1.5 text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-widest">
              Hidden
            </div>
            {hiddenCols.map((col) => (
              <button
                key={col.key}
                type="button"
                onClick={() => onToggleColumn(col.key)}
                className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-[var(--surface)] transition-colors"
              >
                <EyeOff className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                <span className="flex-1 text-left text-sm text-[var(--text-ter)]">{col.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export interface ColumnsControlProps extends Omit<ColumnPickerProps, 'onClose'> {
  /** Controlled open state — a toolbar that closes its other menus when this opens passes both. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** The "Columns" button and its picker. Open state is its own unless the caller controls it. */
export function ColumnsControl({ open: openProp, onOpenChange, ...picker }: ColumnsControlProps) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = openProp ?? ownOpen;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setOwnOpen(next);
    onOpenChange?.(next);
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium  transition-all duration-150 ${
          open
            ? 'text-[var(--text-sec)] bg-[var(--slate-dim)]'
            : 'text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)]'
        }`}
      >
        <Columns3 className="h-3.5 w-3.5" />
        Columns
      </button>
      {open && <ColumnPicker {...picker} onClose={() => setOpen(false)} />}
    </div>
  );
}
